import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-rates-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Types ──────────────────────────────────────────────────────────────── */
type RateAge = '25' | '30' | '35' | '40' | '45' | '50' | '55' | '60';
type RateCoverage = '100k' | '250k' | '500k' | '1m';
type RateType = 'term20' | 'term30' | 'whole' | 'final_expense';
type RateHealth = 'preferred' | 'standard' | 'substandard';

interface RateResult {
  carrier: string;
  amBest: string;
  type: string;
  monthlyRate: number;
  annualRate: number;
  notes: string;
  badge?: string;
}

/* ─── Rate Engine ────────────────────────────────────────────────────────── */
const TERM20_BASE: Record<RateAge, Record<RateCoverage, number>> = {
  '25': { '100k': 12, '250k': 19, '500k': 27, '1m': 46 },
  '30': { '100k': 14, '250k': 23, '500k': 33, '1m': 57 },
  '35': { '100k': 18, '250k': 29, '500k': 43, '1m': 75 },
  '40': { '100k': 26, '250k': 43, '500k': 65, '1m': 114 },
  '45': { '100k': 40, '250k': 67, '500k': 103, '1m': 180 },
  '50': { '100k': 63, '250k': 106, '500k': 164, '1m': 288 },
  '55': { '100k': 103, '250k': 173, '500k': 267, '1m': 467 },
  '60': { '100k': 168, '250k': 282, '500k': 436, '1m': 762 },
};

const TERM30_MULT = 1.42;
const WHOLE_BASE: Record<RateAge, Record<RateCoverage, number>> = {
  '25': { '100k': 68, '250k': 158, '500k': 298, '1m': 580 },
  '30': { '100k': 84, '250k': 196, '500k': 374, '1m': 728 },
  '35': { '100k': 108, '250k': 254, '500k': 490, '1m': 954 },
  '40': { '100k': 148, '250k': 350, '500k': 682, '1m': 1328 },
  '45': { '100k': 204, '250k': 484, '500k': 946, '1m': 1842 },
  '50': { '100k': 284, '250k': 676, '500k': 1324, '1m': 2578 },
  '55': { '100k': 396, '250k': 944, '500k': 1850, '1m': 3604 },
  '60': { '100k': 556, '250k': 1326, '500k': 2600, '1m': 5064 },
};

const FINAL_EXPENSE_BASE: Record<RateAge, Record<RateCoverage, number>> = {
  '25': { '100k': 45, '250k': 110, '500k': 215, '1m': 420 },
  '30': { '100k': 52, '250k': 128, '500k': 250, '1m': 488 },
  '35': { '100k': 62, '250k': 152, '500k': 298, '1m': 580 },
  '40': { '100k': 78, '250k': 192, '500k': 376, '1m': 733 },
  '45': { '100k': 102, '250k': 252, '500k': 494, '1m': 963 },
  '50': { '100k': 138, '250k': 342, '500k': 670, '1m': 1306 },
  '55': { '100k': 188, '250k': 466, '500k': 914, '1m': 1782 },
  '60': { '100k': 258, '250k': 640, '500k': 1254, '1m': 2444 },
};

const HEALTH_MULT: Record<RateHealth, number> = {
  preferred: 1.0,
  standard: 1.28,
  substandard: 1.62,
};

const COVERAGE_AMOUNTS: Record<RateCoverage, number> = {
  '100k': 100000, '250k': 250000, '500k': 500000, '1m': 1000000,
};

function getRateResults(age: RateAge, coverage: RateCoverage, type: RateType, health: RateHealth): RateResult[] {
  const hm = HEALTH_MULT[health];
  const faceAmount = COVERAGE_AMOUNTS[coverage];

  if (type === 'term20') {
    const base = TERM20_BASE[age][coverage];
    const m = Math.round(base * hm);
    return [
      { carrier: 'Banner Life', amBest: 'A+', type: '20-Year Term', monthlyRate: m, annualRate: m * 12, notes: 'Lowest term rates in Indianapolis. Accelerated underwriting up to $1M.', badge: 'Lowest Rate' },
      { carrier: 'Protective Life', amBest: 'A+', type: '20-Year Term', monthlyRate: Math.round(m * 1.03), annualRate: Math.round(m * 1.03) * 12, notes: 'Excellent for 20-year terms. Strong for minor health conditions.' },
      { carrier: 'Pacific Life', amBest: 'A+', type: '20-Year Term', monthlyRate: Math.round(m * 1.06), annualRate: Math.round(m * 1.06) * 12, notes: 'Top pick for $500K+ coverage. Competitive for Indianapolis professionals.' },
      { carrier: 'Prudential', amBest: 'A+', type: '20-Year Term', monthlyRate: Math.round(m * 1.09), annualRate: Math.round(m * 1.09) * 12, notes: 'Best for complex health histories. Lenient underwriting.' },
      { carrier: 'AIG / Corebridge', amBest: 'A', type: '20-Year Term', monthlyRate: Math.round(m * 1.13), annualRate: Math.round(m * 1.13) * 12, notes: 'Competitive for older applicants (45+).' },
    ];
  }

  if (type === 'term30') {
    const base = Math.round(TERM20_BASE[age][coverage] * TERM30_MULT);
    const m = Math.round(base * hm);
    return [
      { carrier: 'Banner Life', amBest: 'A+', type: '30-Year Term', monthlyRate: m, annualRate: m * 12, notes: 'Best 30-year term rates in Indianapolis. Locks in low rates for 30 years.', badge: 'Best Value' },
      { carrier: 'Protective Life', amBest: 'A+', type: '30-Year Term', monthlyRate: Math.round(m * 1.04), annualRate: Math.round(m * 1.04) * 12, notes: 'Excellent 30-year term option. Strong conversion rights.' },
      { carrier: 'Pacific Life', amBest: 'A+', type: '30-Year Term', monthlyRate: Math.round(m * 1.07), annualRate: Math.round(m * 1.07) * 12, notes: 'Top pick for high coverage 30-year policies.' },
      { carrier: 'Prudential', amBest: 'A+', type: '30-Year Term', monthlyRate: Math.round(m * 1.10), annualRate: Math.round(m * 1.10) * 12, notes: 'Good for complex health histories on 30-year terms.' },
      { carrier: 'Lincoln Financial', amBest: 'A+', type: '30-Year Term', monthlyRate: Math.round(m * 1.12), annualRate: Math.round(m * 1.12) * 12, notes: 'Competitive 30-year term rates for Indianapolis families.' },
    ];
  }

  if (type === 'whole') {
    const base = WHOLE_BASE[age][coverage];
    const m = Math.round(base * hm);
    return [
      { carrier: 'Northwestern Mutual', amBest: 'A++', type: 'Whole Life', monthlyRate: m, annualRate: m * 12, notes: 'Highest-rated carrier. Strong dividend history since 1872. Best for wealth building.', badge: 'Top Pick' },
      { carrier: 'MassMutual', amBest: 'A++', type: 'Whole Life', monthlyRate: Math.round(m * 0.97), annualRate: Math.round(m * 0.97) * 12, notes: 'Excellent dividend-paying whole life. Strong cash value accumulation.' },
      { carrier: 'New York Life', amBest: 'A++', type: 'Whole Life', monthlyRate: Math.round(m * 1.02), annualRate: Math.round(m * 1.02) * 12, notes: 'America\'s largest mutual insurer. Consistent dividends.' },
      { carrier: 'Guardian Life', amBest: 'A++', type: 'Whole Life', monthlyRate: Math.round(m * 0.95), annualRate: Math.round(m * 0.95) * 12, notes: 'Competitive whole life rates. Great for Indianapolis business owners.' },
      { carrier: 'Mutual of Omaha', amBest: 'A+', type: 'Whole Life', monthlyRate: Math.round(m * 0.88), annualRate: Math.round(m * 0.88) * 12, notes: 'Most affordable whole life. Non-participating but lower premiums.' },
    ];
  }

  // final_expense
  const base = FINAL_EXPENSE_BASE[age][coverage];
  const m = Math.round(base * hm);
  return [
    { carrier: 'Mutual of Omaha', amBest: 'A+', type: 'Final Expense', monthlyRate: m, annualRate: m * 12, notes: 'Best final expense rates in Indianapolis. Simplified issue — no medical exam.', badge: 'Best Rate' },
    { carrier: 'Foresters Financial', amBest: 'A', type: 'Final Expense', monthlyRate: Math.round(m * 1.04), annualRate: Math.round(m * 1.04) * 12, notes: 'Competitive final expense rates. Strong for Indianapolis seniors.' },
    { carrier: 'AIG / Corebridge', amBest: 'A', type: 'Final Expense', monthlyRate: Math.round(m * 1.07), annualRate: Math.round(m * 1.07) * 12, notes: 'Good final expense option. Guaranteed issue available.' },
    { carrier: 'Transamerica', amBest: 'A', type: 'Final Expense', monthlyRate: Math.round(m * 1.10), annualRate: Math.round(m * 1.10) * 12, notes: 'Solid final expense coverage for Indianapolis seniors.' },
    { carrier: 'Gerber Life', amBest: 'A', type: 'Final Expense', monthlyRate: Math.round(m * 1.14), annualRate: Math.round(m * 1.14) * 12, notes: 'Guaranteed issue — no health questions. Higher rates but guaranteed approval.' },
  ];
}

/* ─── Internal Linking Cluster ───────────────────────────────────────────── */
const CLUSTER_LINKS = [
  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis', icon: 'ri-calculator-line' },
  { to: '/term-life-insurance-rates-indianapolis', label: 'Term Life Insurance Rates Indianapolis', icon: 'ri-bar-chart-line' },
  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/whole-life-insurance-quotes-indianapolis', label: 'Whole Life Insurance Quotes Indianapolis', icon: 'ri-safe-line' },
  { to: '/final-expense-insurance-quotes-indianapolis', label: 'Final Expense Insurance Quotes Indianapolis', icon: 'ri-heart-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
];

/* ─── FAQs ───────────────────────────────────────────────────────────────── */
const FAQS = [
  {
    q: 'What are the average life insurance rates in Indianapolis?',
    a: 'Average life insurance rates in Indianapolis depend on the type of policy, your age, health, and coverage amount. Here are sample monthly rates for a healthy non-smoker: Term life insurance ($500K, 20-year term): Age 30: $33/month. Age 35: $43/month. Age 40: $65/month. Age 45: $103/month. Age 50: $164/month. Whole life insurance ($100K): Age 30: $84/month. Age 35: $108/month. Age 40: $148/month. Age 45: $204/month. Age 50: $284/month. Final expense insurance ($15K): Age 60: $52/month. Age 65: $72/month. Age 70: $98/month. Indianapolis life insurance rates are comparable to national averages — Indiana is not a high-cost state for life insurance. An independent agent compares all available carriers to find your best rate.',
  },
  {
    q: 'What factors affect life insurance rates in Indianapolis?',
    a: 'Life insurance rates in Indianapolis are determined by several key factors: Age: The single biggest factor — rates increase significantly with age. A 25-year-old pays 3–5x less than a 45-year-old for the same coverage. Gender: Women pay 10–15% less than men for the same coverage. Health class: Preferred Plus, Preferred, Standard Plus, Standard, or Table-rated. A Preferred Plus rate can be 30–40% lower than Standard. Smoking status: Smokers pay 2–4x more than non-smokers. Coverage amount: More coverage = higher premium, but the rate per $1,000 of coverage decreases as coverage increases. Term length: Longer terms cost more — a 30-year term is typically 35–45% more expensive than a 20-year term. Policy type: Term life is significantly cheaper than whole life for the same death benefit. Health conditions: Controlled conditions (high blood pressure, diabetes, high cholesterol) result in higher rates. Family history: Heart disease or cancer in parents before age 60 can affect your rate class.',
  },
  {
    q: 'How do I get the lowest life insurance rates in Indianapolis?',
    a: 'To get the lowest life insurance rates in Indianapolis: Buy as young as possible: Rates increase with age — every year you wait costs more. Improve your health before applying: Lose weight, control blood pressure, quit smoking. Work with an independent agent: Independent agents compare all available carriers simultaneously — some carriers are 15–25% cheaper than others for the same coverage. Choose the right policy type: Term life is significantly cheaper than whole life for the same death benefit. Consider accelerated underwriting: Some carriers offer lower rates through accelerated underwriting (no medical exam) for healthy applicants. Quit smoking: After 12 months smoke-free, some carriers reclassify you as a non-smoker — saving 50–70% on premiums. Shop at renewal: If your health has improved since your last policy, you may qualify for better rates. Call Hoosier Life Insurance at (317) 648-2983 for a free rate comparison across all available carriers in Indianapolis.',
  },
  {
    q: 'Are life insurance rates in Indianapolis higher than the national average?',
    a: 'No — life insurance rates in Indianapolis are generally comparable to or slightly below the national average. Indiana is not a high-cost state for life insurance. Factors that affect rates are individual (age, health, coverage amount) rather than geographic. However, the carrier you choose can significantly affect your rate — some carriers are 15–25% cheaper than others for the same coverage in Indianapolis. The key to getting the best rate in Indianapolis is working with an independent agent who can compare all available carriers simultaneously, rather than going directly to a single carrier.',
  },
  {
    q: 'What is the difference between term and whole life insurance rates in Indianapolis?',
    a: 'Term life insurance rates in Indianapolis are significantly lower than whole life insurance rates for the same death benefit. Example comparison for a 35-year-old male in Indianapolis: $500K term life (20-year): $43/month. $500K whole life: $490/month. That\'s an 11x difference in monthly premium. Why the difference: Term life insurance provides coverage for a set period (10–30 years) with no cash value. Whole life insurance provides permanent coverage, builds cash value, and may pay dividends. When term life makes more sense: Income replacement, mortgage protection, covering specific debts, maximum coverage for minimum premium. When whole life makes more sense: Estate planning, business succession, wealth building, permanent coverage needs. For most Indianapolis families with a mortgage and young children, term life insurance provides the best value. An independent agent can help you determine which is right for your situation.',
  },
  {
    q: 'How do life insurance rates change with age in Indianapolis?',
    a: 'Life insurance rates in Indianapolis increase significantly with age. Here\'s how rates change for a $500K 20-year term policy (male, Preferred health): Age 25: $27/month. Age 30: $33/month (+22%). Age 35: $43/month (+30%). Age 40: $65/month (+51%). Age 45: $103/month (+58%). Age 50: $164/month (+59%). Age 55: $267/month (+63%). The rate increase accelerates after age 40 — which is why buying life insurance as early as possible is so important. Every year you wait to buy life insurance in Indianapolis costs more in the long run. A 35-year-old who buys a 30-year term policy locks in their rate for 30 years — they\'ll still be paying $66/month at age 65, while someone who waits until 45 pays $165/month for a 20-year term.',
  },
  {
    q: 'What life insurance rates can I get with health conditions in Indianapolis?',
    a: 'Indianapolis residents with health conditions can still get life insurance — the rates depend on the condition and how well it\'s controlled. Common conditions and their rate impact: High blood pressure (controlled): Standard Plus to Standard rate class — 15–40% above Preferred rates. Type 2 diabetes (controlled, A1C below 7.5): Standard to Table 2 — 40–100% above Preferred rates. High cholesterol (controlled): Standard Plus — 15–25% above Preferred rates. Sleep apnea (treated with CPAP): Standard Plus to Standard. Overweight/obesity (BMI 30–35): Standard Plus to Standard. The key: different carriers treat the same condition differently. An independent agent who specializes in impaired risk underwriting can find the best carrier for your specific health history in Indianapolis. Call (317) 648-2983 for a free consultation.',
  },
  {
    q: 'How do I compare life insurance rates in Indianapolis?',
    a: 'Comparing life insurance rates in Indianapolis requires looking at more than just the monthly premium. Key factors to compare: Monthly premium: The amount you pay each month. Rate class: Preferred Plus, Preferred, Standard Plus, or Standard — the rate class determines your premium. AM Best rating: Financial strength of the carrier — stick with A or A+ rated carriers. Policy features: Conversion options, riders available, accelerated underwriting. Carrier reputation: Claims payment history, customer service ratings. The most important step: work with an independent agent who can compare all available carriers simultaneously. Our Rate Comparison Tool above provides estimated rates for Indianapolis residents. Call (317) 648-2983 for exact quotes from all available carriers.',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  {
    name: 'Kevin & Lisa M.',
    location: 'Carmel, Indianapolis',
    quote: 'We were paying $187/month with our old carrier. Benjamin compared 8 carriers and found us the same $750K coverage for $112/month. That\'s $900 a year back in our pocket.',
    stars: 5,
    savings: 'Saved $75/month',
    coverage: '$750K 20-year term',
  },
  {
    name: 'Patricia H.',
    location: 'Noblesville, Indianapolis',
    quote: 'I have high blood pressure and was quoted $340/month by one company. Benjamin knew which carriers are lenient with BP and found me $500K for $198/month. Huge difference.',
    stars: 5,
    savings: 'Saved $142/month',
    coverage: '$500K 20-year term',
  },
  {
    name: 'James & Sandra R.',
    location: 'Fishers, Indianapolis',
    quote: 'We needed whole life for estate planning. Benjamin compared 5 carriers and found us Northwestern Mutual at a rate we could actually afford. The cash value projections were incredible.',
    stars: 5,
    savings: 'Best whole life rate',
    coverage: '$500K whole life each',
  },
  {
    name: 'Dorothy W.',
    location: 'Broad Ripple, Indianapolis',
    quote: 'I\'m 68 and needed final expense coverage. Benjamin found me Mutual of Omaha for $89/month — $15K coverage, no medical exam. Approved in 10 minutes.',
    stars: 5,
    savings: 'Approved instantly',
    coverage: '$15K final expense',
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */
export default function LifeInsuranceRatesIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const [rAge, setRAge] = useState<RateAge>('35');
  const [rCoverage, setRCoverage] = useState<RateCoverage>('500k');
  const [rType, setRType] = useState<RateType>('term20');
  const [rHealth, setRHealth] = useState<RateHealth>('preferred');
  const [results, setResults] = useState<RateResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Life Insurance Rates Indianapolis IN | Rate Comparison Tool | Hoosier Life Insurance';
    const description = 'Compare life insurance rates in Indianapolis, IN. Use our Rate Comparison Tool to see term, whole life, and final expense rates from top carriers. Call (317) 648-2983.';
    const keywords = 'life insurance rates Indianapolis, life insurance rates Indianapolis Indiana, life insurance cost Indianapolis, compare life insurance rates Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/52d9c57f8ec02c9c8cb2d483d0de008d.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance Rates Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: RateAge[] = ['25', '30', '35', '40', '45', '50', '55', '60'];
  const coverageOptions: { value: RateCoverage; label: string }[] = [
    { value: '100k', label: '$100K' },
    { value: '250k', label: '$250K' },
    { value: '500k', label: '$500K' },
    { value: '1m', label: '$1M' },
  ];
  const typeOptions: { value: RateType; label: string; desc: string; icon: string }[] = [
    { value: 'term20', label: '20-Year Term', desc: 'Most popular — affordable, fixed rate', icon: 'ri-calendar-check-line' },
    { value: 'term30', label: '30-Year Term', desc: 'Longest coverage — best for young families', icon: 'ri-calendar-2-line' },
    { value: 'whole', label: 'Whole Life', desc: 'Permanent coverage + cash value', icon: 'ri-safe-line' },
    { value: 'final_expense', label: 'Final Expense', desc: 'Simplified issue — no medical exam', icon: 'ri-heart-line' },
  ];
  const healthOptions: { value: RateHealth; label: string; desc: string }[] = [
    { value: 'preferred', label: 'Preferred', desc: 'Good health, minor issues' },
    { value: 'standard', label: 'Standard', desc: 'Average health, some conditions' },
    { value: 'substandard', label: 'Substandard', desc: 'Health conditions present' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Life Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Life Insurance Rates</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20professional%20financial%20advisor%20showing%20life%20insurance%20rate%20comparison%20charts%20to%20couple%20at%20modern%20office%20desk%20warm%20natural%20light%20Marion%20County%20Indiana%20confident%20professional%20consultation%20photorealistic%20warm%20neutral%20tones%20cream%20beige%20background%20wide%20angle&width=1440&height=700&seq=li-rates-indy-hero-2025a&orientation=landscape"
            alt="Life Insurance Rates Indianapolis Indiana — Rate Comparison Tool"
            title="Life Insurance Rates in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-line-chart-line text-sm"></i>
              Indianapolis &amp; Marion County · Term · Whole Life · Final Expense · Rate Comparison
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              2026 Life Insurance Rates —{' '}
              <span className="text-[#D97642]">Real Pricing for Indianapolis Families</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Compare life insurance rates across all policy types in Indianapolis. Use our <strong className="text-white">Rate Comparison Tool</strong> to see term life, whole life, and final expense rates from the top carriers side by side.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '$18/mo', label: 'Lowest life insurance rate in Indianapolis' },
                { value: '4 Types', label: 'Term, whole life, final expense compared' },
                { value: '5+', label: 'Top carriers compared for each policy type' },
                { value: 'Free', label: 'Rate comparison — no obligation' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#rate-tool" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-line-chart-line text-lg"></i>
                Compare My Rates
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$18/mo', label: 'Lowest term life rate — healthy 30-year-old, $250K, 20-year term' },
              { value: '15–25%', label: 'Typical savings when comparing carriers vs. going direct to one company' },
              { value: 'A/A++', label: 'All carriers rated A or higher by AM Best — financially strong' },
              { value: '20+', label: 'Carriers compared to find your best Indianapolis life insurance rate' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Life Insurance Rates in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Indianapolis life insurance rates are comparable to national averages — Indiana is not a high-cost state',
                  'The carrier you choose can vary rates by 15–25% for the same coverage — comparing carriers is essential',
                  'Age is the single biggest factor in life insurance rates — every year you wait costs more',
                  'Women pay 10–15% less than men for the same life insurance coverage in Indianapolis',
                  'Smokers pay 2–4x more than non-smokers — quitting for 12 months can dramatically reduce your rate',
                  'An independent agent compares 20+ carriers simultaneously to find your best Indianapolis rate',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT */}
          <div>

            {/* RATE COMPARISON TOOL */}
            <div id="rate-tool" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Rate Comparison Tool</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Life Insurance Rates — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage, policy type, and health class to see estimated rates from the top carriers in Indianapolis.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-line-chart-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  {/* Age */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setRAge(a); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${rAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setRCoverage(opt.value); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${rCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Policy Type */}
                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Policy Type</label>
                    <div className="grid grid-cols-2 gap-3">
                      {typeOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setRType(opt.value); setResults(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${rType === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="flex items-center gap-2 mb-1">
                            <i className={`${opt.icon} text-sm`}></i>
                            <span className="text-sm font-bold">{opt.label}</span>
                          </div>
                          <div className={`text-xs ${rType === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Health */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Health Class</label>
                    <div className="grid grid-cols-3 gap-3">
                      {healthOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setRHealth(opt.value); setResults(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${rHealth === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${rHealth === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setResults(getRateResults(rAge, rCoverage, rType, rHealth))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-line-chart-line text-lg"></i>
                    Compare My Life Insurance Rates
                  </button>
                </div>

                {/* Results */}
                {results !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        {typeOptions.find(t => t.value === rType)?.label} Rates — ${COVERAGE_AMOUNTS[rCoverage].toLocaleString()} · Age {rAge} · {rHealth.charAt(0).toUpperCase() + rHealth.slice(1)} Health
                      </h3>
                      {results.length > 0 && (
                        <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                          From ${Math.min(...results.map(q => q.monthlyRate))}/mo
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-4 mb-6">
                      {results.map((r, i) => (
                        <div key={r.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{r.carrier}</span>
                                  {r.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{r.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {r.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{r.notes}</p>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${r.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${r.annualRate.toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-[#1A3A52]/5 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates are estimates for illustration purposes. Actual rates depend on full underwriting and carrier selection. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized rate.
                      </p>
                    </div>

                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Rate — Free 15-Min Call
                    </button>
                  </div>
                )}

                {results === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-line-chart-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your profile above, then click <strong className="text-[#1A3A52]">Compare My Life Insurance Rates</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Rate Factors */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Rate Factors</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                What Affects Life Insurance Rates in Indianapolis?
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { factor: 'Age', impact: 'Very High', desc: 'Rates increase 3–8% per year. A 45-year-old pays 2–3x more than a 30-year-old for the same coverage.', icon: 'ri-time-line', color: 'text-red-500' },
                  { factor: 'Health Class', impact: 'Very High', desc: 'Preferred Plus vs. Standard can be a 30–40% rate difference. Health conditions can double or triple rates.', icon: 'ri-heart-pulse-line', color: 'text-red-500' },
                  { factor: 'Smoking Status', impact: 'Very High', desc: 'Smokers pay 2–4x more than non-smokers. Quitting for 12 months can dramatically reduce your rate.', icon: 'ri-forbid-line', color: 'text-red-500' },
                  { factor: 'Gender', impact: 'Moderate', desc: 'Women pay 10–15% less than men for the same coverage — women have longer life expectancy.', icon: 'ri-user-line', color: 'text-[#D97642]' },
                  { factor: 'Coverage Amount', impact: 'Moderate', desc: 'More coverage = higher premium, but the rate per $1,000 decreases as coverage increases.', icon: 'ri-money-dollar-circle-line', color: 'text-[#D97642]' },
                  { factor: 'Term Length', impact: 'Moderate', desc: 'A 30-year term is 35–45% more expensive than a 20-year term for the same coverage amount.', icon: 'ri-calendar-line', color: 'text-[#D97642]' },
                  { factor: 'Policy Type', impact: 'Very High', desc: 'Whole life is 8–12x more expensive than term life for the same death benefit — but builds cash value.', icon: 'ri-file-list-3-line', color: 'text-red-500' },
                  { factor: 'Carrier Choice', impact: 'Moderate', desc: 'Different carriers rate the same applicant differently — comparing carriers can save 15–25%.', icon: 'ri-building-2-line', color: 'text-[#D97642]' },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                        <i className={`${item.icon} text-base text-[#D97642]`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-sm font-bold text-[#1A3A52]">{item.factor}</span>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.impact === 'Very High' ? 'bg-red-50 text-red-500' : 'bg-[#D97642]/10 text-[#D97642]'} whitespace-nowrap`}>
                            {item.impact} Impact
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Residents Who Found Better Rates
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.savings}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">
                Explore All Indianapolis Life Insurance Topics
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Compare rates, explore coverage options, and find the right policy for your Indianapolis family.
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Life Insurance Rates Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-li-rates-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-line-chart-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Rate Comparison</h3>
                    <p className="text-xs text-white/70">20+ carriers compared · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ All policy types</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ All carriers compared</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No obligation</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                  <Link to="/instant-quote"
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-[#1A3A52] rounded-xl hover:bg-gray-200 transition-all cursor-pointer font-semibold text-sm">
                    <i className="ri-file-list-3-line"></i>
                    Get Instant Online Quote
                  </Link>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · All ages welcome</p>
              </div>
            </div>

            {/* Rate Snapshot */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                Rate Snapshot — Indianapolis
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { type: '$250K 20-yr Term', age: 'Age 30', rate: '$23/mo', color: 'text-[#4A7C59]' },
                  { type: '$500K 20-yr Term', age: 'Age 35', rate: '$43/mo', color: 'text-[#4A7C59]' },
                  { type: '$500K 20-yr Term', age: 'Age 40', rate: '$65/mo', color: 'text-[#4A7C59]' },
                  { type: '$100K Whole Life', age: 'Age 40', rate: '$148/mo', color: 'text-[#D97642]' },
                  { type: '$15K Final Expense', age: 'Age 65', rate: '$72/mo', color: 'text-[#1A3A52]' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <div>
                      <div className="text-xs font-semibold text-[#1A3A52]">{row.type}</div>
                      <div className="text-xs text-gray-400">{row.age}</div>
                    </div>
                    <span className={`text-sm font-bold ${row.color}`}>{row.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Preferred health class, male, non-smoker. Rates vary by carrier.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Rate Call</h3>
                    <p className="text-xs text-white/70">All carriers compared · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Rate Comparison Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Policy type or questions? (Optional)',
                    submitButton: 'Confirm My Free Rate Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Quotes Indianapolis', icon: 'ri-calculator-line' },
                  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
                  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance', icon: 'ri-money-dollar-circle-line' },
                  { to: '/life-insurance-quotes-indianapolis', label: 'Compare All Carriers', icon: 'ri-search-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Ready to compare life insurance rates in Indianapolis?
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Rate Comparison Tool above, then call for your exact personalized rate. 20+ carriers compared. No obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#rate-tool" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-line-chart-line text-lg"></i>
              Compare My Rates
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={118} productName="Life Insurance Rates Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Compare Life Insurance Rates — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
